import React, { useEffect, useState } from 'react';
import { theme } from '../theme';
import { useResponsive } from '../hooks/useResponsive';
import { getApiUrl } from '../config';
import type { Friend } from '../types';

interface FriendStats {
  totalMatches: number;
  totalGoals: number;
  totalAssists: number;
  totalSaves: number;
  totalWins: number;
  totalLosses: number;
  totalDraws: number;
  preferredPosition?: string;
}

const normalizeStats = (raw: any): FriendStats => {
  const n = (v: unknown) => (Number(v ?? 0) || 0);
  return {
    totalMatches: n(raw?.totalMatches),
    totalGoals: n(raw?.totalGoals),
    totalAssists: n(raw?.totalAssists),
    totalSaves: n(raw?.totalSaves),
    totalWins: n(raw?.totalWins ?? raw?.wins),
    totalLosses: n(raw?.totalLosses ?? raw?.losses),
    totalDraws: n(raw?.totalDraws ?? raw?.draws),
    preferredPosition: raw?.preferredPosition,
  };
};

interface FriendActionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  friend: Friend | null;
  onViewProfile: (friendId: string) => void;
  onRemoveFriend: (friendId: string) => void | Promise<void>;
  removing?: boolean;
}

export const FriendActionsModal: React.FC<FriendActionsModalProps> = ({
  isOpen,
  onClose,
  friend,
  onViewProfile,
  onRemoveFriend,
  removing,
}) => {
  const { isMobile } = useResponsive();
  const [stats, setStats] = useState<FriendStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);

  useEffect(() => {
    if (!isOpen || !friend) return;
    let cancelled = false;
    setLoadingStats(true);
    setStats(null);
    const token = localStorage.getItem('authToken');
    fetch(getApiUrl(`/api/users/${friend.id}/stats`), {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    })
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        if (cancelled) return;
        if (data) {
          const raw = data.stats ?? data;
          const normalized = normalizeStats(raw);
          if (data.user?.position) normalized.preferredPosition = data.user.position;
          setStats(normalized);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoadingStats(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, friend]);

  if (!isOpen || !friend) return null;

  const initials = friend.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();

  const handleRemove = () => {
    const ok = window.confirm(
      `¿Estás seguro que querés eliminar a ${friend.name} de tus amigos? Se eliminará de ambos lados.`
    );
    if (ok) {
      onRemoveFriend(friend.id);
    }
  };

  const handleViewProfile = () => {
    onViewProfile(friend.id);
    onClose();
  };

  const renderStar = (i: number) => {
    const filled = i < Math.floor(friend.rating);
    return (
      <span key={i} style={{ color: '#FFD700', fontSize: '14px' }}>
        {filled ? '★' : '☆'}
      </span>
    );
  };

  return (
    <div
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: isMobile ? 'flex-end' : 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: isMobile ? 0 : '20px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: theme.colors.surface,
          borderRadius: isMobile ? '20px 20px 0 0' : theme.borderRadius.card,
          padding: theme.spacing.lg,
          maxWidth: isMobile ? '100%' : '460px',
          width: '100%',
          maxHeight: isMobile ? '90vh' : '85vh',
          overflowY: 'auto',
          boxShadow: theme.colors.shadow,
          border: `1px solid ${theme.colors.border}`,
        }}
      >
        {/* Handle bar (mobile) */}
        {isMobile && (
          <div
            style={{
              width: '40px',
              height: '4px',
              backgroundColor: theme.colors.border,
              borderRadius: '2px',
              margin: '0 auto 16px',
            }}
          />
        )}

        {/* Close button (desktop) */}
        {!isMobile && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
            <button
              onClick={onClose}
              aria-label="Cerrar"
              style={{
                background: 'none',
                border: 'none',
                fontSize: '22px',
                cursor: 'pointer',
                color: theme.colors.textSecondary,
                padding: '4px 8px',
              }}
            >
              ×
            </button>
          </div>
        )}

        {/* Friend header */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '20px',
          }}
        >
          {friend.avatar ? (
            <img
              src={friend.avatar}
              alt={friend.name}
              style={{
                width: '72px',
                height: '72px',
                borderRadius: '50%',
                objectFit: 'cover',
                border: `2px solid ${theme.colors.primary}`,
              }}
            />
          ) : (
            <div
              style={{
                width: '72px',
                height: '72px',
                borderRadius: '50%',
                backgroundColor: theme.colors.primary,
                color: '#000',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '24px',
                fontWeight: 700,
                fontFamily: theme.fonts.heading,
              }}
            >
              {initials}
            </div>
          )}
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: theme.colors.text, fontSize: '20px', fontWeight: 700 }}>
              {friend.name}
            </div>
            {(stats?.preferredPosition || friend.preferredPosition) && (
              <div style={{ color: theme.colors.textSecondary, fontSize: '13px', marginTop: 4 }}>
                {stats?.preferredPosition || friend.preferredPosition}
              </div>
            )}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                marginTop: '6px',
              }}
            >
              {Array.from({ length: 5 }).map((_, i) => renderStar(i))}
              <span
                style={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: theme.colors.text,
                  marginLeft: '4px',
                }}
              >
                {friend.rating.toFixed(1)}/5
              </span>
            </div>
          </div>
        </div>

        {/* Stats grid */}
        {loadingStats ? (
          <div
            style={{
              textAlign: 'center',
              color: theme.colors.textSecondary,
              fontSize: '13px',
              padding: '24px',
            }}
          >
            Cargando stats...
          </div>
        ) : stats ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '8px',
              backgroundColor: theme.colors.background,
              padding: '12px',
              borderRadius: '12px',
              marginBottom: '20px',
            }}
          >
            <StatCell label="GOLES" value={stats.totalGoals} />
            <StatCell label="ASISTENCIAS" value={stats.totalAssists} />
            <StatCell label="ATAJADAS" value={stats.totalSaves} />
            <StatCell label="PARTIDOS" value={stats.totalMatches} />
            <StatCell label="GANADOS" value={stats.totalWins} valueColor="#10B981" />
            <StatCell label="PERDIDOS" value={stats.totalLosses} valueColor="#EF4444" />
          </div>
        ) : null}

        {/* Actions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <button
            onClick={handleViewProfile}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '14px 16px',
              backgroundColor: theme.colors.background,
              border: `2px solid ${theme.colors.primary}`,
              borderRadius: '12px',
              cursor: 'pointer',
              textAlign: 'left',
              width: '100%',
            }}
          >
            <span style={{ fontSize: '22px' }}>👤</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '15px', fontWeight: 600, color: theme.colors.text }}>
                Ver perfil completo
              </div>
              <div style={{ fontSize: '12px', color: theme.colors.textSecondary }}>
                Historial y estadísticas
              </div>
            </div>
            <span style={{ color: theme.colors.textSecondary, fontSize: '18px' }}>›</span>
          </button>

          <button
            onClick={handleRemove}
            disabled={removing}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '14px 16px',
              backgroundColor: theme.colors.background,
              border: `2px solid #EF4444`,
              borderRadius: '12px',
              cursor: removing ? 'not-allowed' : 'pointer',
              opacity: removing ? 0.6 : 1,
              textAlign: 'left',
              width: '100%',
            }}
          >
            <span style={{ fontSize: '22px' }}>🗑</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '15px', fontWeight: 600, color: '#EF4444' }}>
                {removing ? 'Eliminando...' : 'Eliminar amigo'}
              </div>
              <div style={{ fontSize: '12px', color: theme.colors.textSecondary }}>
                Se eliminará de ambos lados
              </div>
            </div>
            <span style={{ color: '#EF4444', fontSize: '18px' }}>›</span>
          </button>

          <button
            onClick={onClose}
            style={{
              marginTop: '4px',
              padding: '12px',
              background: 'transparent',
              border: 'none',
              color: theme.colors.textSecondary,
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
};

const StatCell: React.FC<{ label: string; value: number; valueColor?: string }> = ({
  label,
  value,
  valueColor,
}) => (
  <div
    style={{
      backgroundColor: theme.colors.surface,
      padding: '10px 8px',
      borderRadius: '8px',
      textAlign: 'center',
    }}
  >
    <div
      style={{
        fontSize: '20px',
        fontWeight: 700,
        color: valueColor || theme.colors.text,
      }}
    >
      {value}
    </div>
    <div
      style={{
        fontSize: '10px',
        fontWeight: 600,
        color: theme.colors.textSecondary,
        marginTop: '2px',
      }}
    >
      {label}
    </div>
  </div>
);

export default FriendActionsModal;
